import RegularButton from '@/components/buttons/RegularButton'
import Divider from '@/components/divider/Divider'
import Image from '@/components/image/Image'
import theme from '@/components/themes/mainTheme'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Grid from '@mui/material/Grid2'
import ImageList from '@mui/material/ImageList'
import ImageListItem from '@mui/material/ImageListItem'
import React from 'react'
import { useMediaQuery } from 'react-responsive'
import { useNavigate, useSearchParams } from 'react-router-dom'
import galleryData from './galleryImages.json'

interface BaseImage {
  path: string
  description?: string
  source?: string
}

interface ImageGroup {
  title: string
  description?: string
  images: BaseImage[]
}

type GalleryImageItem = BaseImage | ImageGroup

interface GalleryItem {
  header: string
  breadcrumb: string
  images: GalleryImageItem[]
}

const typedGalleryData = galleryData as Record<string, GalleryItem>

const isImageGroup = (item: GalleryImageItem): item is ImageGroup => {
  return 'images' in item && 'title' in item
}

const ImageGallery: React.FC = () => {
  const isMobile = useMediaQuery({
    query: `(max-width: ${theme.breakpoints.values.md}px)`,
  })
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const selector = searchParams.get('selector')

  const currentGallery = selector ? typedGalleryData[selector] : undefined

  return (
    <>
      <Grid
        container
        direction="row"
        columnSpacing={2}
        justifyContent="center"
        alignItems="center"
        textAlign="center"
      >
        <Grid size={10} textAlign="start" marginTop="20px">
          <RegularButton
            label={
              <>
                <ArrowBackIcon /> Tillbaka
              </>
            }
            onClick={() => navigate('/gallery-selector')}
          />
        </Grid>

        {!currentGallery ? (
          <Grid size={10}>
            <h1>Inga bilder här :(</h1>
          </Grid>
        ) : (
          <>
            <Grid size={10}>
              <h1>{currentGallery.header}</h1>
            </Grid>
            <Grid size={{ xs: 11, md: 10 }}>
              <ImageList cols={isMobile ? 1 : 4} gap={50}>
                {currentGallery.images.map((item, index) => {
                  if (isImageGroup(item)) {
                    return (
                      <Grid
                        size={12}
                        key={`group-${index}`}
                        style={{ gridColumn: '1 / -1', marginTop: '20px' }}
                      >
                        <h2 style={{ textAlign: 'left', marginBottom: '20px' }}>
                          {item.title}
                        </h2>
                        <p style={{ textAlign: 'left', marginBottom: '20px' }}>
                          {item.description}
                        </p>
                        <ImageList cols={isMobile ? 1 : 4} gap={50}>
                          {item.images.map((subImage, subIndex) => (
                            <ImageListItem key={`sub-${subIndex}`}>
                              <Image
                                src={`${subImage.path}?w=164&fit=crop&auto=format&dpr=2 2x`}
                                alt={`${item.title} ${subIndex}`}
                                caption={subImage.description}
                                photographer={subImage.source}
                                clickable
                              />
                            </ImageListItem>
                          ))}
                        </ImageList>
                        <Divider />
                      </Grid>
                    )
                  }

                  return (
                    <ImageListItem key={`direct-${index}`}>
                      <Image
                        src={`${item.path}?w=164&fit=crop&auto=format&dpr=2 2x`}
                        alt={`Bildlista ${index}`}
                        caption={item.description}
                        photographer={item.source}
                        clickable
                      />
                    </ImageListItem>
                  )
                })}
              </ImageList>
            </Grid>
          </>
        )}
      </Grid>
    </>
  )
}

export default ImageGallery
